import { t } from "@lingui/macro"
import { CircularProgress } from "@mui/material"
import { observer } from "mobx-react"
import { useCallback, useMemo } from "react"

import { DEFAULT_ACCESS_GROUP } from "src/config"

import { GlobalStore } from "src/store"
import { useStore } from "src/store/lib/useStore"
import { SessionStore } from "src/store/session"
import { AutoCompleteTextField } from "src/components/AutoCompleteTextField"

export const HeadAccessGroupSelect = observer(() => {
    const gstore = useStore(GlobalStore)

    const accessGroups = useMemo(
        () => [DEFAULT_ACCESS_GROUP, ...gstore.session.accessGroups],
        [gstore],
    )
    const accessGroupLabels = useMemo(() => {
        return Object.fromEntries(accessGroups.map((ag) => [ag.id, ag.name]))
    }, [accessGroups])

    const hasNoAccessGroups = gstore.session.accessGroups.length === 0
    const loading = gstore.loading.is(
        SessionStore.LoadingKeys["reload-access-group-segments"],
    )
    const disabled = hasNoAccessGroups || loading

    // Transform options to format expected by AutoCompleteTextField
    const formattedOptions = useMemo(() => {
        return accessGroups.map(({ id }) => ({
            id: id,
            label: accessGroupLabels[String(id)] ?? "",
        }))
    }, [accessGroups, accessGroupLabels])

    // Get current selected option object
    const selectedOption = useMemo(() => {
        const currentId =
            gstore.session.accessGroup.id ?? DEFAULT_ACCESS_GROUP.id

        // Find the matching option and return it or null
        return (
            formattedOptions.find((option) => option.id === currentId) ?? null
        )
    }, [formattedOptions, gstore.session.accessGroup.id])

    const handleSelectAccessGroup = useCallback(
        async (_, value) => {
            // Return early if no value
            if (value == null) return

            // Extract the ID from the selected option
            const selectedId =
                typeof value === "object" ? value.id : Number(value)

            const accessGroup = accessGroups.find(({ id }) => id === selectedId)

            if (accessGroup != null) {
                await gstore.session.setSelectedAccessGroup(accessGroup)
            } else {
                // TODO: do this in a better way
                throw new Error("accessgroup could not be selected.")
            }
        },
        [accessGroups, gstore],
    )

    return (
        <AutoCompleteTextField
            options={formattedOptions}
            size="medium"
            onChange={handleSelectAccessGroup}
            value={selectedOption}
            title={t`head.access-group-label`}
            placeholder={t`head.access-group-label`}
            popupIcon={
                loading ? (
                    <CircularProgress size={16} color="inherit" />
                ) : undefined
            }
            disabled={disabled}
            autoHighlight
            blurOnSelect
            disableClearable
            sx={{ width: 300 }}
        />
    )
})
